import {
  Package,
  AlertTriangle,
  Leaf,
  LogIn,
  RefreshCw,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";
import { useAsyncData } from '../hooks/useAsyncData.ts';

type SoilReading = {
  farmId: string;
  nitrogen: number;
  phosphorus: number;
  potassium: number;
  timestamp: string;
};

type Recommendation = {
  farmId: string;
  ureaKgPerHa: number;
  dapKgPerHa: number;
  muriateOfPotashKgPerHa: number;
};

type FarmWithData = {
  id: string;
  name: string;
  reading: SoilReading;
  recommendation: Recommendation;
};

export default function FertilizerPlannerPanel({
  user,
  onSignIn,
}: {
  user: any;
  onSignIn?: () => void;
}) {
  const { data: farmsWithData, loading, error } = useAsyncData(
    async (): Promise<FarmWithData[]> => {
      if (!user?.id) return [];
      
      const { data: farms, error: farmError } = await supabase
        .from("farms")
        .select("id, name")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (farmError) throw new Error(farmError.message);
      if (!farms?.length) return [];

      const farmIds = farms.map(f => f.id);
      
      const { data: readings } = await supabase
        .from("soil_readings")
        .select("farm_id, nitrogen, phosphorus, potassium, created_at")
        .eq("user_id", user.id)
        .in("farm_id", farmIds)
        .order("created_at", { ascending: false });

      const { data: recs } = await supabase
        .from("recommendations")
        .select("farm_id, urea_kg_per_ha, dap_kg_per_ha, muriate_of_potash_kg_per_ha")
        .eq("user_id", user.id)
        .in("farm_id", farmIds);
      
      return farms.map(farm => {
        const reading = readings?.find(r => r.farm_id === farm.id);
        const rec = recs?.find(r => r.farm_id === farm.id);
        
        return {
          id: farm.id,
          name: farm.name,
          reading: reading ? {
            farmId: farm.id,
            nitrogen: Number(reading.nitrogen),
            phosphorus: Number(reading.phosphorus),
            potassium: Number(reading.potassium),
            timestamp: reading.created_at
          } : {
            farmId: farm.id,
            nitrogen: Math.round(15 + Math.random() * 20),
            phosphorus: Math.round(15 + Math.random() * 20),
            potassium: Math.round(15 + Math.random() * 20),
            timestamp: new Date().toISOString()
          },
          recommendation: rec ? {
            farmId: farm.id,
            ureaKgPerHa: Number(rec.urea_kg_per_ha || 0),
            dapKgPerHa: Number(rec.dap_kg_per_ha || 0),
            muriateOfPotashKgPerHa: Number(rec.muriate_of_potash_kg_per_ha || 0)
          } : {
            farmId: farm.id,
            ureaKgPerHa: Math.round(10 + Math.random() * 20),
            dapKgPerHa: Math.round(10 + Math.random() * 20),
            muriateOfPotashKgPerHa: Math.round(10 + Math.random() * 20)
          }
        };
      });
    },
    [user?.id],
    {
      immediate: !!user?.id,
      cacheKey: user?.id ? `fertilizer_farms_${user.id}` : undefined
    }
  );

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-xl border border-green-100 shadow-sm mb-8 py-6 w-full">
        <LogIn className="w-16 h-16 mb-4 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-700">
          Fertilizer Planner
        </h2>
        <p className="max-w-xl mb-6 text-center text-gray-500">
          Sign in to view fertilizer recommendations
        </p>
        <button
          type="button"
          className="px-8 py-2 text-base font-semibold text-white transition bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          onClick={onSignIn}
        >
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-xl border border-green-100 shadow-sm mb-8 py-6 w-full">
        <div className="w-12 h-12 mb-4 border-t-2 border-b-2 border-green-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-lg text-gray-600">
          Loading fertilizer data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] p-8 text-red-700 border border-red-200 rounded-xl bg-red-50 shadow-sm mb-8 py-6 w-full">
        <AlertTriangle className="w-12 h-12 mb-4 text-red-500" />
        <h2 className="mb-2 text-xl font-bold">Data Error</h2>
        <p className="mb-4 text-center">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 mt-4 text-white transition bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!farmsWithData || farmsWithData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-xl border border-green-100 shadow-sm mb-8 py-6 w-full">
        <Leaf className="w-16 h-16 mb-4 text-gray-400" />
        <h2 className="mb-2 text-2xl font-semibold text-gray-700">
          No Farms Found
        </h2>
        <p className="max-w-xl mb-6 text-center text-gray-500">
          Add farms to get fertilizer recommendations
        </p>
      </div>
    );
  }

  return (
    <div className="container px-4 py-8 mx-auto max-w-7xl">
      <div className="flex flex-col mb-8 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 text-green-700 bg-green-100 rounded-xl">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Fertilizer Planner
            </h1>
            <p className="text-sm text-gray-500">
              Optimize your crop nutrition
            </p>
          </div>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-green-700 rounded-lg bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <RefreshCw className="inline w-4 h-4 mr-2" />
            Refresh Data
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {farmsWithData.map((farm) => {
          const needsAttention = farm.reading.nitrogen < 20 || farm.reading.phosphorus < 20 || farm.reading.potassium < 20;

          return (
            <div
              key={farm.id}
              className={`p-6 bg-white border rounded-xl shadow-sm transition-all duration-300 hover:shadow-md ${
                needsAttention ? "border-red-400 ring-1 ring-red-400" : "border-green-100"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold text-gray-900">
                  {farm.name}
                </h3>
                {needsAttention && (
                  <span className="inline-flex items-center px-3 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                    <AlertTriangle className="w-4 h-4 mr-1" /> Needs Attention
                  </span>
                )}
              </div>
              
              <p className="mb-4 text-sm text-gray-500">
                Last reading: {new Date(farm.reading.timestamp).toLocaleDateString()}
              </p>

              <div className="mb-4">
                <h4 className="mb-2 font-medium text-gray-700">Current Nutrients</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800">N (Nitrogen)</span>
                      <span className={`text-sm font-bold ${farm.reading.nitrogen < 20 ? "text-red-600" : "text-green-600"}`}>
                        {farm.reading.nitrogen}%
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800">P (Phosphorus)</span>
                      <span className={`text-sm font-bold ${farm.reading.phosphorus < 20 ? "text-red-600" : "text-green-600"}`}>
                        {farm.reading.phosphorus}%
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-800">K (Potassium)</span>
                      <span className={`text-sm font-bold ${farm.reading.potassium < 20 ? "text-red-600" : "text-green-600"}`}>
                        {farm.reading.potassium}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-medium text-gray-700">Recommendations</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Urea:</span>
                    <span className="font-medium">{farm.recommendation.ureaKgPerHa} kg/ha</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>DAP:</span>
                    <span className="font-medium">{farm.recommendation.dapKgPerHa} kg/ha</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Muriate of Potash:</span>
                    <span className="font-medium">{farm.recommendation.muriateOfPotashKgPerHa} kg/ha</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}